import { z } from "zod";

export enum AppUsageType {
  APP_OPEN = "APP_OPEN",
  PROFILE_VIEW = "PROFILE_VIEW",
  IMAGE_VIEW = "IMAGE_VIEW",
  IMAGE_LIKE = "IMAGE_LIKE",
}

// APP_OPEN
export type AppOpenDataBody = {
  type: AppUsageType.APP_OPEN;
};

export const AppOpenDataBodySchema = z.object({
  type: z.literal(AppUsageType.APP_OPEN),
});

// PROFILE_VIEW
export type ProfileViewDataBody = {
  type: AppUsageType.PROFILE_VIEW;
  target: string;
  durationInSeconds: number;
};

export const ProfileViewDataBodySchema = z.object({
  type: z.literal(AppUsageType.PROFILE_VIEW),
  target: z.string(),
  durationInSeconds: z.number().min(0),
});

// IMAGE_VIEW
export type ImageViewDataBody = {
  type: AppUsageType.IMAGE_VIEW;
  imageId: string;
  durationInSeconds: number;
};

export const ImageViewDataBodySchema = z.object({
  type: z.literal(AppUsageType.IMAGE_VIEW),
  imageId: z.string(),
  durationInSeconds: z.number().min(0),
});

// IMAGE_LIKE
export type ImageLikeDataBody = {
  type: AppUsageType.IMAGE_LIKE;
  imageId: string;
};

export const ImageLikeDataBodySchema = z.object({
  type: z.literal(AppUsageType.IMAGE_LIKE),
  imageId: z.string(),
});

// data
export type AppUsageDataBody = {
  type: AppUsageType;
  appOpenData?: AppOpenDataBody;
  profileViewData?: ProfileViewDataBody;
  imageViewData?: ImageViewDataBody;
  imageLikeData?: ImageLikeDataBody;
};

export const AppUsageDataBodySchema = z
  .object({
    type: z.nativeEnum(AppUsageType),
    appOpenData: AppOpenDataBodySchema.optional(),
    profileViewData: ProfileViewDataBodySchema.optional(),
    imageViewData: ImageViewDataBodySchema.optional(),
    imageLikeData: ImageLikeDataBodySchema.optional(),
  })
  .refine((data) => {
    switch (data.type) {
      case AppUsageType.APP_OPEN:
        return (
          !!data.appOpenData &&
          AppOpenDataBodySchema.safeParse(data.appOpenData).success
        );
      case AppUsageType.PROFILE_VIEW:
        return (
          !!data.profileViewData &&
          ProfileViewDataBodySchema.safeParse(data.profileViewData).success
        );
      case AppUsageType.IMAGE_VIEW:
        return (
          !!data.imageViewData &&
          ImageViewDataBodySchema.safeParse(data.imageViewData).success
        );
      case AppUsageType.IMAGE_LIKE:
        return (
          !!data.imageLikeData &&
          ImageLikeDataBodySchema.safeParse(data.imageLikeData).success
        );
      default:
        return false;
    }
  });

// event body
export type LogAppUsageEventBody = {
  eventId: string;
  startTime: string;
  deviceId: string;
  username: string | null;
  data: AppUsageDataBody;
};

export const LogAppUsageEventBodySchema = z.object({
  eventId: z.string().uuid(),
  startTime: z.string().datetime({ offset: true }),
  deviceId: z.string().uuid(),
  username: z.string().nullable(),
  data: AppUsageDataBodySchema,
});

// request body
export type LogAppUsageRequestBody = {
  events: LogAppUsageEventBody[];
};

export const LogAppUsageRequestBodySchema = z.object({
  events: z.array(LogAppUsageEventBodySchema).min(1),
});
